import { PartnershipTransaction } from "@axl-dev/school-sdk";
import { Button, Form, Popconfirm } from "antd";
import { useWatch } from "antd/es/form/Form";
import { useCallback, useState } from "react";

import { useLoc, useNotifier, useSdk } from "@app/shared/lib/hooks";
import { Loc, Price } from "@app/shared/ui";
import { FormBuilder } from "@app/shared/ui/FormBuilder";

import MaxAvailableAmount from "../MaxAvailableAmount/MaxAvailableAmount";

export type TransferToBonusFormProps = {
  minAmount?: number;
  onSuccess: () => void;
  onCancel: () => void;
};

export const TransferToBonusForm: React.FC<TransferToBonusFormProps> = ({
  minAmount,
  onSuccess,
  onCancel,
}) => {
  const loc = useLoc();
  const sdk = useSdk();
  const [maxAmount, setMaxAmount] = useState(0);
  const [nofitier] = useNotifier();
  const [isCreating, setCreating] = useState(false);

  const [form] = Form.useForm();
  const amount = useWatch("amount", form);

  const transferToBonus = useCallback(
    async (values: { amount: number | null }) => {
      setCreating(true);
      const result = await sdk.partnership.transactionStore.create(
        new PartnershipTransaction({
          amount: values.amount ?? 0,
          useBonus: true,
        }),
      );
      if (result.success) onSuccess();
      nofitier(
        result,
        loc.word("Partnership.transaction.transferToBonus.success", {
          default: "Transaction has been successfully created",
        }),
      );
      setCreating(false);
    },
    [onSuccess],
  );

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={transferToBonus}
      initialValues={{ amount: minAmount ?? 0 }}
    >
      <FormBuilder.InputNumber
        name="amount"
        rules={(b) =>
          b
            .required()
            .minNumber(minAmount ?? 0)
            .maxNumber(maxAmount)
        }
        label={
          <Loc
            word="Partnership.transaction.transferToBonus.amount"
            default="Enter amount to transfer"
          />
        }
        extra={
          <MaxAvailableAmount
            onReady={setMaxAmount}
            onClick={() => {
              form.setFieldValue("amount", maxAmount);
            }}
          />
        }
      />

      <div className="flex gap-10">
        <Button onClick={onCancel} disabled={isCreating}>
          <Loc word="Global.cancel" default="Cancel" />
        </Button>
        <Popconfirm
          disabled={amount === 0}
          title={
            <Loc
              word="Partnership.transaction.transferToBonus.confirm"
              default="Are you sure you want to transfer {0} to bonus account?"
            >
              <Price price={amount} />
            </Loc>
          }
          onConfirm={form.submit}
        >
          <Button type="primary" disabled={amount === 0} loading={isCreating}>
            <Loc
              word="Partnership.transaction.transferToBonus"
              default="Transfer to bonus account"
            />
          </Button>
        </Popconfirm>
      </div>
    </Form>
  );
};
